import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * Session-backed viewer shape extracted from JWT claims after login.
 */
export interface SessionViewer {
  id: string;
  name: string;
  email: string;
  userName: string;
}

type SessionStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextType {
  viewer: SessionViewer | null;
  sessionStatus: SessionStatus;
  userHasLoggedIn: () => boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  getAccessTokenAsync: () => Promise<string>;
  authenticationInProgress: boolean;
  loginError: string | null;
}

export const AuthContext: React.Context<AuthContextType | undefined> = createContext<
  AuthContextType | undefined
>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const SESSION_STORAGE_KEY = "anex.session.viewer";
const SESSION_TOKEN_STORAGE_KEY = "anex.session.access-token";

/** Minimal JWT payload shape we extract after login. */
interface JwtPayload {
  oid: string;
  name: string;
  email: string;
  preferred_username: string;
  exp: number;
}

function decodeJwtPayload(token: string): JwtPayload {
  const segments = token.split(".");
  if (segments.length !== 3) {
    throw new Error("Malformed JWT: expected 3 segments");
  }

  // The middle segment is the payload (base64url-encoded)
  const base64 = segments[1].replace(/-/g, "+").replace(/_/g, "/");

  const json = atob(base64);
  const parsed: unknown = JSON.parse(json);

  if (
    typeof parsed !== "object" ||
    parsed === null ||
    !("oid" in parsed) ||
    !("name" in parsed) ||
    !("email" in parsed) ||
    !("preferred_username" in parsed) ||
    !("exp" in parsed)
  ) {
    throw new Error(
      "JWT payload missing required claims (oid, name, email, preferred_username, exp)",
    );
  }

  return parsed as JwtPayload;
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwtPayload(token);
    return payload.exp < Date.now() / 1000;
  } catch {
    return true;
  }
}

function readStoredViewer(): SessionViewer | null {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !("id" in parsed) ||
      !("name" in parsed) ||
      !("email" in parsed) ||
      !("userName" in parsed)
    ) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return parsed as SessionViewer;
  } catch (_error: unknown) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }
}

function readStoredToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
}

function getApiBaseUrl(): string {
  return process.env.REACT_APP_API_URL ?? "";
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }: AuthProviderProps) => {
  const [viewer, setViewer] = useState<SessionViewer | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>("loading");
  const [loginError, setLoginError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  const userHasLoggedIn = useCallback((): boolean => {
    return viewer !== null;
  }, [viewer]);

  const signIn = useCallback(async (username: string, password: string): Promise<void> => {
    setLoginError(null);

    const url = `${getApiBaseUrl()}/auth/login`;
    let response: Response;

    try {
      response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Network error";
      setLoginError(message);
      throw new Error(message);
    }

    if (response.status === 401) {
      const message = "Invalid credentials";
      setLoginError(message);
      throw new Error(message);
    }

    if (!response.ok) {
      let message = `Login failed (${response.status})`;
      try {
        const body: unknown = await response.json();
        if (
          typeof body === "object" &&
          body !== null &&
          "message" in body &&
          typeof (body as { message: unknown }).message === "string"
        ) {
          message = (body as { message: string }).message;
        }
      } catch {
        // Response body could not be parsed as JSON — use the default message
      }
      setLoginError(message);
      throw new Error(message);
    }

    const body: unknown = await response.json();
    if (
      typeof body !== "object" ||
      body === null ||
      !("token" in body) ||
      typeof (body as { token: unknown }).token !== "string"
    ) {
      const message = "Unexpected login response: missing token";
      setLoginError(message);
      throw new Error(message);
    }

    const token = (body as { token: string }).token;
    const payload = decodeJwtPayload(token);

    const nextViewer: SessionViewer = {
      id: payload.oid,
      name: payload.name,
      email: payload.email,
      userName: payload.preferred_username,
    };

    // Store token in memory (primary) and localStorage (persistence)
    tokenRef.current = token;
    window.localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token);
    window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(nextViewer));

    setViewer(nextViewer);
    setSessionStatus("authenticated");
  }, []);

  const signOut = useCallback((): void => {
    tokenRef.current = null;
    window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    setViewer(null);
    setLoginError(null);
    setSessionStatus("anonymous");
  }, []);

  const getAccessTokenAsync = useCallback(async (): Promise<string> => {
    // Primary: in-memory token (check expiry before returning)
    if (tokenRef.current !== null) {
      if (isTokenExpired(tokenRef.current)) {
        tokenRef.current = null;
      } else {
        return tokenRef.current;
      }
    }

    // Fallback: localStorage (e.g. after page refresh before state hydrates)
    const stored = readStoredToken();
    if (stored !== null && !isTokenExpired(stored)) {
      tokenRef.current = stored;
      return stored;
    }

    throw new Error("No access token available. Please sign in.");
  }, []);

  // Restore session from localStorage on mount
  useEffect(() => {
    const storedToken = readStoredToken();
    const storedViewer = readStoredViewer();

    if (storedToken !== null && storedViewer !== null) {
      if (isTokenExpired(storedToken)) {
        // Token expired — clear stale session data
        window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
        setSessionStatus("anonymous");
      } else {
        tokenRef.current = storedToken;
        setViewer(storedViewer);
        setSessionStatus("authenticated");
      }
    } else {
      // No complete session data — clean up any partial remnants
      window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      setSessionStatus("anonymous");
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        viewer,
        sessionStatus,
        userHasLoggedIn,
        signIn,
        signOut,
        getAccessTokenAsync,
        authenticationInProgress: sessionStatus === "loading",
        loginError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
